// Portfolio Administration v1.0 PR 3 §6 — popover that opens when a Loan
// pill is clicked. Mirrors the deal flow's DealAssignmentPopover but
// uses the Loan-specific RBAC rules:
//
//   • Assign B1        → E1, E2, ADMIN
//   • Assign B3        → B4 (own portfolio), E2, ADMIN
//   • Assign B4        → E1, E2, ADMIN
//   • Assign E-VM      → E5, ADMIN
//   • Assign E2 / E1   → ADMIN only
//   • Assign B5        → B5 lead, E1, ADMIN
//   • Mark NA          → E1, ADMIN
//
// The server enforces permissions authoritatively; this widget gates the
// affordances so the UX doesn't tease the user with un-actionable rows.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import { RoleFilteredUserPicker } from "@/components/DealFlow/common/RoleFilteredUserPicker";
import { useUpsertLoanAssignment } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import {
  LOAN_ROLE_LABEL,
  type LoanAssignmentDto,
  type LoanAssignmentStatus,
  type LoanRoleCode,
} from "@/api/portfolioAdmin/types";

interface AssignmentPillPopoverProps {
  loanId: string;
  role: LoanRoleCode;
  assignment: LoanAssignmentDto | undefined;
  trigger: ReactNode;
}

export function AssignmentPillPopover({
  loanId,
  role,
  assignment,
  trigger,
}: AssignmentPillPopoverProps) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (popoverRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative inline-block">
      <div ref={triggerRef} onClick={() => setOpen((v) => !v)}>
        {trigger}
      </div>
      {open && (
        <div
          ref={popoverRef}
          className="absolute z-40 top-full left-0 mt-space-1 w-[300px] bg-white-pure border border-border-light rounded-md shadow-lg p-space-3"
          role="dialog"
          aria-label={`Assign ${role}`}
          data-testid={`loan-pill-popover-${role}`}
        >
          <PopoverContent
            loanId={loanId}
            role={role}
            assignment={assignment}
            onClose={() => setOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

function canAssignRole(
  role: LoanRoleCode,
  userRoleCodes: string[],
): boolean {
  const has = (code: string) => userRoleCodes.includes(code);
  if (has("ADMIN")) return true;
  switch (role) {
    case "B1":
      return has("E1") || has("E2");
    case "B3":
      return has("B4") || has("E2");
    case "B4":
      return has("E1") || has("E2");
    case "E1-VM":
      return has("E5");
    case "E2":
    case "E1":
      return false;
    case "B5":
      // "B5 lead" not modeled as a separate role code; treat any B5 as
      // lead-capable for self-claim.
      return has("E1") || has("B5");
    default:
      return false;
  }
}

function canMarkNotApplicable(userRoleCodes: string[]): boolean {
  return userRoleCodes.includes("ADMIN") || userRoleCodes.includes("E1");
}

function canSelfClaim(role: LoanRoleCode, userRoleCodes: string[]): boolean {
  // Self-claim allowed for B5 (when unassigned) per spec §6.
  if (role === "B5") return userRoleCodes.includes("B5");
  return false;
}

interface PopoverContentProps {
  loanId: string;
  role: LoanRoleCode;
  assignment: LoanAssignmentDto | undefined;
  onClose: () => void;
}

function PopoverContent({ loanId, role, assignment, onClose }: PopoverContentProps) {
  const auth = useAuth();
  const toast = useToast();
  const userRoleCodes = auth.user?.roleCodes ?? [];

  const currentStatus: LoanAssignmentStatus =
    (assignment?.status as LoanAssignmentStatus) ?? "Unassigned";
  const currentUserId = assignment?.assignedUserId ?? null;

  const canEdit = canAssignRole(role, userRoleCodes);
  const canNa = canMarkNotApplicable(userRoleCodes);
  const canSelfClaimNow =
    currentStatus === "Unassigned" && canSelfClaim(role, userRoleCodes);

  const [status, setStatus] = useState<LoanAssignmentStatus>(currentStatus);
  const [userId, setUserId] = useState<string | null>(currentUserId);

  const upsert = useUpsertLoanAssignment(loanId);

  const onSave = async () => {
    try {
      if (status === "NotApplicable") {
        await upsert.mutateAsync({
          roleCode: role,
          body: { action: "not-applicable", justification: "(no justification supplied)" },
        });
        toast.success(`${role} marked Not Applicable.`);
      } else if (status === "Unassigned") {
        await upsert.mutateAsync({
          roleCode: role,
          body: { action: "unassign" },
        });
        toast.success(`${role} cleared.`);
      } else {
        await upsert.mutateAsync({
          roleCode: role,
          body: { action: "assign", assignedUserId: userId },
        });
        toast.success(`${role} assigned.`);
      }
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not update assignment.",
      );
    }
  };

  const onSelfClaim = async () => {
    if (!auth.user?.id) return;
    try {
      await upsert.mutateAsync({
        roleCode: role,
        body: { action: "assign", assignedUserId: auth.user.id },
      });
      toast.success(`Claimed ${role}.`);
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Could not claim slot.",
      );
    }
  };

  const saveDisabled =
    !canEdit ||
    upsert.isPending ||
    (status === "Assigned" && !userId);

  return (
    <div className="flex flex-col gap-space-3">
      <div>
        <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow">
          {role}
        </div>
        <div className="font-display text-h4 text-onyx">{LOAN_ROLE_LABEL[role]}</div>
      </div>

      {canSelfClaimNow && (
        <Button variant="primary" onClick={() => void onSelfClaim()} disabled={upsert.isPending}>
          Claim this slot
        </Button>
      )}

      <fieldset className="flex flex-col gap-space-2 m-0 p-0 border-0">
        <legend className="sr-only">Slot status</legend>
        <RadioRow
          name={`loan-assignment-${role}`}
          value="Unassigned"
          checked={status === "Unassigned"}
          onChange={() => setStatus("Unassigned")}
          disabled={!canEdit}
          label="Unassigned"
        />
        <RadioRow
          name={`loan-assignment-${role}`}
          value="Assigned"
          checked={status === "Assigned"}
          onChange={() => setStatus("Assigned")}
          disabled={!canEdit}
          label="Assigned to:"
        />
        {canNa && (
          <RadioRow
            name={`loan-assignment-${role}`}
            value="NotApplicable"
            checked={status === "NotApplicable"}
            onChange={() => setStatus("NotApplicable")}
            disabled={!canEdit && !canNa}
            label="Not applicable for this loan"
          />
        )}
      </fieldset>

      {status === "Assigned" && (
        <div>
          <label className="block text-caption text-gray-mid mb-space-1">User</label>
          <RoleFilteredUserPicker
            role={role}
            value={userId}
            onChange={setUserId}
            disabled={!canEdit}
            aria-label={`Select ${role}`}
          />
        </div>
      )}

      <div className="flex items-center justify-end gap-space-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => void onSave()}
          disabled={saveDisabled}
          title={!canEdit ? "You do not have permission to edit this assignment." : undefined}
        >
          {upsert.isPending ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function RadioRow({
  name,
  value,
  checked,
  onChange,
  label,
  disabled,
}: {
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex items-center gap-space-2 text-small ${
        disabled ? "opacity-50" : "cursor-pointer"
      }`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <span>{label}</span>
    </label>
  );
}
